<script setup lang="ts">
import { $isCodeHighlightNode } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $isListItemNode, $isListNode, type ListType } from "@lexical/list";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isTextNode,
  COLLABORATION_TAG,
  COMMAND_PRIORITY_HIGH,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  type RangeSelection,
  TextNode,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

import type TextEditorControls from "~/components/text/TextEditorControls.vue";
import { RecommendationKind } from "~/shared/enums";
import { usePageStore, useUserStore } from "~/stores";
import { BACKGROUND_ALPHA, DEFAULT_HIGHLIGHT_COLOR } from "~/utils/color";

import {
  EVENT_FORMAT_BACKGROUND_COLOR,
  EVENT_FORMAT_TEXT_COLOR,
  EVENT_GENERATE_RECOMMENDATION,
  EVENT_INSERT_HORIZONTAL_RULE,
  EVENT_INSERT_TOGGLE_COMMAND,
  EVENT_OPEN_RECOMMENDATIONS,
  TEXT_REPLACEMENT_RULES,
} from "../const";
import { HORIZONTAL_RULE_TRANSFORMER } from "../nodes/HorizontalRuleNode";
import { $isTypeaheadNode } from "../nodes/TypeaheadNode";
import {
  createTextBlockNode,
  formatBulletList,
  formatCheckList,
  formatNumberedList,
  getSelectedNode,
  getUpdateDetails,
  insertTable,
  isLineLevelNode,
} from "../utils";

const TEXT_COLOR_REGEX = /(?:^|;)\s*color:\s*[^;\s][^;]*/;
const BACKGROUND_COLOR_REGEX = /(?:^|;)\s*background-color:\s*[^;\s][^;]*/;

const props = defineProps<{
  textEditorControls?: InstanceType<typeof TextEditorControls> | null;
}>();

const emit = defineEmits<{
  editLast: [];
}>();

const pageStore = usePageStore();
const userStore = useUserStore();
const editor = useLexicalComposer();

const insertLink = (selection: RangeSelection) => {
  const node = getSelectedNode(selection);
  const parent = node.getParent();

  if ($isLinkNode(parent) || $isLinkNode(node)) {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    return;
  }

  editor.dispatchCommand(TOGGLE_LINK_COMMAND, "");
};

const handleTextReplacements = (node: TextNode, offset: number) => {
  editor.update(() => {
    if ($isCodeHighlightNode(node) || node.hasFormat("code")) {
      return;
    }

    const content = node.getTextContent();
    const lastContent = content.slice(0, offset);
    TEXT_REPLACEMENT_RULES.forEach(([pattern, replacement, requiresWordBoundary]) => {
      const match = pattern.exec(lastContent);
      if (!match) {
        return;
      }

      const matchText = match[0];
      const matchStart = match.index;

      if (requiresWordBoundary) {
        const charBeforePattern = lastContent[matchStart - 1];
        if (charBeforePattern !== undefined && /\w/.test(charBeforePattern)) {
          return;
        }
      }

      const matchEnd = matchStart + matchText.length;
      node.setTextContent(`${content.slice(0, matchStart)}${replacement}${content.slice(matchEnd)}`);
      const newSelect = matchStart + replacement.length;
      node.select(newSelect, newSelect);
    });
  });
};

const handleMoveUp = (event: KeyboardEvent): boolean => {
  const ctrl = pageStore.isMac ? event.metaKey : event.ctrlKey;
  if (!ctrl || !event.altKey) {
    return false;
  }
  event.preventDefault();

  editor.update(() => {
    const selection = $getSelection() as RangeSelection;
    if (selection === null) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const nearestLineLevelParent = $findMatchingParent(anchorNode, isLineLevelNode);
    const base = nearestLineLevelParent;
    if (base === null) {
      return;
    }
    const prev = base.getPreviousSibling();
    if (prev === null || !$isElementNode(prev)) {
      return;
    }
    const prevBaseMaybe = prev.getPreviousSibling();
    const next = base.getNextSibling();
    const last = next !== null && $isElementNode(next) && $isListNode(next.getChildren()[0]) ? next : base;
    last.insertAfter(prev);
    if (!$isListNode(prev.getChildren()[0])) {
      return;
    }
    if (prevBaseMaybe === null) {
      return;
    }
    last.insertAfter(prevBaseMaybe);
  });

  return true;
};

const handleMoveDown = (event: KeyboardEvent): boolean => {
  const ctrl = pageStore.isMac ? event.metaKey : event.ctrlKey;
  if (!ctrl || !event.altKey) {
    return false;
  }
  event.preventDefault();

  editor.update(() => {
    const selection = $getSelection() as RangeSelection;
    if (selection === null) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const nearestLineLevelParent = $findMatchingParent(anchorNode, isLineLevelNode);
    const base = nearestLineLevelParent;
    if (base === null) {
      return;
    }
    const next = base.getNextSibling();
    if (next === null) {
      return;
    }
    const nextBase = !$isElementNode(next) || !$isListNode(next.getChildren()[0]) ? next : next.getNextSibling();
    if (nextBase === null) {
      return;
    }
    const nextChildMaybe = nextBase.getNextSibling();
    base.insertBefore(nextBase);
    if (nextChildMaybe === null || !$isElementNode(nextChildMaybe) || !$isListNode(nextChildMaybe.getChildren()[0])) {
      return;
    }
    base.insertBefore(nextChildMaybe);
  });

  return true;
};

// TODO should these all be commands?
const handleKeyboardShortcuts = (event: KeyboardEvent): boolean => {
  const selection = $getSelection() as RangeSelection;
  if (selection === null || $isNodeSelection(selection)) {
    return false;
  }

  const { key, code } = event;
  const ctrl = pageStore.isMac ? event.metaKey : event.ctrlKey;
  const alt = event.altKey;
  const ctrlAndShift = ctrl && event.shiftKey;
  const ctrlAndAlt = ctrl && alt;

  const anchorNode = selection.anchor.getNode();
  const topLevelElement = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
  const nearestLineLevelParent = $findMatchingParent(anchorNode, isLineLevelNode);
  const listType: ListType | null = $isListNode(topLevelElement) ? topLevelElement.getListType() : null;

  if (key === "Enter" && !ctrl && !alt && !event.shiftKey && $isListItemNode(nearestLineLevelParent)) {
    const hrMatch = nearestLineLevelParent.getTextContent().match(HORIZONTAL_RULE_TRANSFORMER.regExp);
    if (selection.isCollapsed() && hrMatch && selection.anchor.offset === anchorNode.getTextContentSize()) {
      event.preventDefault();
      HORIZONTAL_RULE_TRANSFORMER.replace(nearestLineLevelParent, [], hrMatch, false);
      return true;
    }
  }

  if (ctrl && !event.shiftKey && !event.altKey && key === "ArrowUp") {
    event.preventDefault();
    emit("editLast");
    return true;
  }

  if (props.textEditorControls && ctrl && alt && code === "KeyA") {
    event.preventDefault();
    props.textEditorControls.startRecording();
    return true;
  }

  if (ctrlAndShift) {
    switch (key) {
      case "s":
      case "S": {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
        return true;
      }
      case "h": {
        event.preventDefault();
        const node = selection.anchor.getNode();
        if (!$isTextNode(node)) {
          return true;
        }

        const recentStack = userStore.recentColorCombinedStack[0];
        const isValid = recentStack?.color?.trim() && recentStack.color !== "default";
        const color = isValid ? recentStack.color : DEFAULT_HIGHLIGHT_COLOR;
        const isBg = isValid ? recentStack.isBackground : true;

        const style = node.getStyle() || "";
        const hasColor = isBg ? BACKGROUND_COLOR_REGEX.test(style) : TEXT_COLOR_REGEX.test(style);

        if (!hasColor) {
          userStore.addToRecentColorStack(isValid ? color : DEFAULT_HIGHLIGHT_COLOR, isValid ? isBg : true);
        }
        editor.dispatchCommand(
          isBg ? EVENT_FORMAT_BACKGROUND_COLOR : EVENT_FORMAT_TEXT_COLOR,
          hasColor ? "" : isBg ? `${color}${BACKGROUND_ALPHA}` : color
        );
        return true;
      }
      case "6": {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
        return true;
      }
      case "7": {
        event.preventDefault();
        formatNumberedList(listType, editor);
        return true;
      }
      case "8": {
        event.preventDefault();
        formatBulletList(listType, editor);
        return true;
      }
      case "9": {
        event.preventDefault();
        formatCheckList(listType, editor);
        return true;
      }
      default: {
        return false;
      }
    }
  }

  if (ctrlAndAlt) {
    switch (code) {
      case "KeyX": {
        event.preventDefault();
        if (listType !== "check") {
          formatCheckList(listType, editor);
          return true;
        }
        const selectedNodes = selection.getNodes();
        let checkListNodes = selectedNodes.filter($isListItemNode);
        if (checkListNodes.length === 0 && nearestLineLevelParent !== null && $isListItemNode(nearestLineLevelParent)) {
          checkListNodes = [nearestLineLevelParent];
        }
        checkListNodes.forEach((e) => e.setChecked(!e.getChecked()));
        return true;
      }
      case "KeyI": {
        event.preventDefault();
        editor.dispatchCommand(EVENT_OPEN_RECOMMENDATIONS, undefined);
        return true;
      }
      case "KeyU": {
        event.preventDefault();
        editor.dispatchCommand(EVENT_GENERATE_RECOMMENDATION, {
          kind: RecommendationKind.CUSTOM,
          parentKey: null,
        });
        return true;
      }
      case "KeyK":
      case "ArrowUp": {
        return handleMoveUp(event);
      }
      case "KeyJ":
      case "ArrowDown": {
        return handleMoveDown(event);
      }
      case "Digit0": {
        event.preventDefault();
        createTextBlockNode("paragraph");
        return true;
      }
      case "Digit1": {
        event.preventDefault();
        createTextBlockNode("h1");
        return true;
      }
      case "Digit2": {
        event.preventDefault();
        createTextBlockNode("h2");
        return true;
      }
      case "Digit3": {
        event.preventDefault();
        createTextBlockNode("h3");
        return true;
      }
      case "Digit4": {
        event.preventDefault();
        createTextBlockNode("code");
        return true;
      }
      case "Digit5": {
        event.preventDefault();
        createTextBlockNode("quote");
        return true;
      }
      case "Digit6": {
        event.preventDefault();
        editor.dispatchCommand(EVENT_INSERT_TOGGLE_COMMAND, null);
        return true;
      }
      case "Digit7": {
        event.preventDefault();
        editor.dispatchCommand(EVENT_INSERT_HORIZONTAL_RULE, null);
        return true;
      }
      case "Digit8": {
        event.preventDefault();
        insertTable(editor);
        return true;
      }
      default: {
        return false;
      }
    }
  }

  if (ctrl) {
    switch (key) {
      case "l": {
        event.preventDefault();
        insertLink(selection);
        return true;
      }
      default: {
        return false;
      }
    }
  }
  return false;
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerUpdateListener((payload) => {
      if (payload.tags.has(COLLABORATION_TAG)) {
        return;
      }
      const { normal, anchorNode, anchorOffset } = getUpdateDetails(payload);
      if (
        !normal ||
        anchorNode === undefined ||
        !($isTextNode(anchorNode) || $isTypeaheadNode(anchorNode)) ||
        anchorOffset === undefined
      ) {
        return;
      }
      handleTextReplacements(anchorNode, anchorOffset);
    }),
    editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (props.textEditorControls?.working) {
          if (event.key === "Enter") {
            event.preventDefault();
            props.textEditorControls.finishRecording();
            return true;
          }
          if (event.key === "Escape") {
            event.preventDefault();
            props.textEditorControls.cancelRecording();
            return true;
          }
        }

        const ctrl = pageStore.isMac ? event.metaKey : event.ctrlKey;
        if (!ctrl && event.key !== "Enter") {
          return false;
        }
        return handleKeyboardShortcuts(event);
      },
      COMMAND_PRIORITY_HIGH
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
